import { useMemo, useEffect } from 'react';
import {
    useEventGroup,
    useEventItem,
    useEventGroupsLoadDone,
    useEventsLoadDone,
    useEventsChunkRequest,
    useMarketsChunkRequest,
    useMarketsLoadDone,
} from 'modules/common/hooks';
import MojitoPresentation from 'mojito/presentation';
import MojitoServices from 'mojito/services';
import { isEmpty, pick } from 'mojito/utils';
import MojitoCore from 'mojito/core';
import PriceBoostMarketContent from 'modules/aggregated-market-pane/price-boost/content/index.js';
import { useAggregatedMarket } from 'modules/aggregated-market-pane/hooks';
import EventListUtils from 'modules/event-list/utils.js';
import NavigationBarButton from 'modules/event-list/navigation-bar-button/index.js';
import { extractCouponSportId } from 'modules/cms-sport-coupon/utils.js';
import PriceBoostCouponSkeleton from './skeleton/index.js';
import { shallowEqual, useSelector } from 'react-redux';

const {
    FlexPane,
    Text,
    Button,
    EventTimingStatus,
    LoaderSuspense,
    ParticipantIconsInline,
    scoreboardUtils: { sortParticipants },
} = MojitoPresentation.Components;
const { getSeparatorTranslationKey } = MojitoPresentation.Utils.ParticipantSeparator;
const { EventFormatter } = MojitoPresentation.Utils;
const { IntentActionTrigger } = MojitoPresentation.Base.Intent;
const { NavigationPathBuilder } = MojitoPresentation.Base.Navigation;
const { utils: MarketGroupUtils } = MojitoServices.SportsContent.MarketGroups;
const { utils: EventsUtils } = MojitoServices.SportsContent.Events;
const VideoStreamsUtils = MojitoServices.VideoStreaming.utils;
const AnalyticsContextBuilder = MojitoCore.Presentation.AnalyticsContext.ContextBuilder;
const AnalyticsContextExtender = MojitoCore.Presentation.AnalyticsContext.ContextExtender;
const { selectEvents } = MojitoServices.SportsContent.Events.selectors;
const { selectSingleLeagueGroups, selectEventGroupName } = MojitoServices.EventGroups.selectors;
const EMPTY_OBJECT = {};

const resolveBoostedCouponSportId = ({ sportId, boostedGroupId, boostedGroups }) =>
    sportId ||
    boostedGroups?.[0]?.linkedSportId ||
    extractCouponSportId(boostedGroupId) ||
    boostedGroups?.[0]?.id;

const PriceBoostCoupon = props => {
    const {
        boostedGroupId,
        sportId,
        showSportTitles,
        mojitoTools: { config, appContext, instanceId, stringResolver },
        onDataLoad,
        selectedGroupId,
    } = props;
    const { maxEventsToRender } = config;

    const allBoostedGroups = useEventGroup(boostedGroupId);
    const boostedGroups = useMemo(() => {
        const groups = selectedGroupId
            ? allBoostedGroups?.filter(group => group.id === selectedGroupId)
            : allBoostedGroups;

        if (maxEventsToRender > -1) {
            return EventListUtils.resolveViewableGroups(-1, maxEventsToRender, groups);
        }
        return groups;
    }, [selectedGroupId, allBoostedGroups, maxEventsToRender]);

    const preloadDone = useBoostedContentPreload(boostedGroups, instanceId);
    const boostedGroupLoadDone = useEventGroupsLoadDone([boostedGroupId]);
    const singleLeagueGroups = useSelector(state =>
        selectSingleLeagueGroups(boostedGroupId, state)
    );
    const eventGroupName = useSelector(state => selectEventGroupName(boostedGroupId, state));
    const noContent = boostedGroupLoadDone && isEmpty(boostedGroups);
    const loadDone = noContent || (boostedGroupLoadDone && preloadDone);

    const analyticsContext = useMemo(
        () =>
            new AnalyticsContextBuilder()
                .withAnalyticsPath(
                    `PriceBoostCoupon${selectedGroupId ? ' on a sports page pivot' : ''}`
                )
                .build(),
        [selectedGroupId]
    );

    useEffect(() => {
        loadDone && onDataLoad(boostedGroupId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadDone]);

    const skeleton = <PriceBoostCouponSkeleton config={config.priceBoostCouponSkeleton} />;

    if (noContent) {
        return null;
    }

    const resolvedSportId = resolveBoostedCouponSportId({
        sportId,
        boostedGroupId,
        boostedGroups: allBoostedGroups,
    });
    const totalEventCount = allBoostedGroups?.flatMap(group => group.events)?.length;
    const renderedEventCount = boostedGroups?.flatMap(group => group.events)?.length ?? 0;
    const isTruncated = totalEventCount > renderedEventCount;
    const showAllEventsButton =
        config.enableRedirectToCouponPage && maxEventsToRender > -1 && isTruncated;
    const resolvedSportName = showAllEventsButton
        ? appContext.sportName(resolvedSportId)
        : undefined;
    const navigationButtonLabel =
        resolvedSportName && eventGroupName
            ? EventListUtils.resolveEventGroupNavigationLabel(
                  eventGroupName,
                  resolvedSportName,
                  totalEventCount,
                  stringResolver
              )
            : undefined;

    return (
        <AnalyticsContextExtender value={analyticsContext}>
            <LoaderSuspense
                config={config.skeletonLoader}
                isContentPending={!loadDone}
                loader={skeleton}
            >
                <FlexPane config={config.eventCouponContainer}>
                    {boostedGroups && (
                        <FlexPane config={config.container} class={'ta-PriceBoostCoupon'}>
                            {boostedGroups.map(group => (
                                <BoostedEventsGroup
                                    key={group.id}
                                    appContext={appContext}
                                    group={group}
                                    config={config}
                                    shouldRequestData={preloadDone}
                                    showSportTitle={showSportTitles}
                                    stringResolver={stringResolver}
                                />
                            ))}
                        </FlexPane>
                    )}
                    <NavigationBarButton
                        config={config.navigationBarButton}
                        eventGroupsId={boostedGroupId}
                        sportId={resolvedSportId}
                        sportName={resolvedSportName}
                        redirectToTypePage={singleLeagueGroups}
                        label={navigationButtonLabel}
                        renderedEventCount={renderedEventCount}
                        typeId={allBoostedGroups?.[0]?.typeId}
                        showAllEventsButton={showAllEventsButton}
                        isCouponNavigation={true}
                    />
                </FlexPane>
            </LoaderSuspense>
        </AnalyticsContextExtender>
    );
};

const BoostedEventsGroup = ({
    group,
    showSportTitle,
    config,
    appContext,
    shouldRequestData,
    stringResolver,
}) => {
    return (
        <FlexPane config={config.eventsGroupContainer}>
            {showSportTitle && (
                <Text config={config.sportNameTitle}>{appContext.sportName(group.id)}</Text>
            )}
            {!isEmpty(group.events) && (
                <FlexPane config={config.eventsContainer}>
                    {group.events.map(boostedEvent => (
                        <BoostedEvent
                            key={boostedEvent.id}
                            event={boostedEvent}
                            config={config}
                            routeResolver={appContext.routeResolver}
                            shouldRequestData={shouldRequestData}
                            linkedSportId={group.linkedSportId}
                            stringResolver={stringResolver}
                        />
                    ))}
                </FlexPane>
            )}
        </FlexPane>
    );
};

const BoostedEvent = ({
    event,
    config,
    routeResolver,
    shouldRequestData,
    linkedSportId,
    stringResolver,
}) => {
    const eventItem = useEventItem(event.id, shouldRequestData) || {};
    const { boostedMarketIds, sportId, id: eventId, details } = eventItem;
    const isRacing = EventsUtils.isRaceEventType(event.eventType);
    const isOutright = EventsUtils.isOutrightEventType(event.eventType);
    const showEventName = isRacing || isOutright;
    const onHeaderClick = () => {
        isRacing
            ? IntentActionTrigger.showMeetingsRaces(sportId, details.meetingId, eventId)
            : IntentActionTrigger.showSportEvent(linkedSportId || sportId, eventId);
    };
    const linkToEvent = NavigationPathBuilder.sportEventPath(
        linkedSportId || sportId,
        eventId,
        routeResolver
    );
    const marketInfo =
        boostedMarketIds && MarketGroupUtils.createPriceBoostedMarketInfo(boostedMarketIds);
    const boostedAggregatedMarket = useAggregatedMarket(marketInfo, shouldRequestData);

    const participants = useMemo(
        () => sortParticipants(eventItem.participants, eventItem.homeAway),
        [eventItem.participants, eventItem.homeAway]
    );

    if (!boostedAggregatedMarket) {
        return null;
    }

    return (
        <FlexPane config={config.eventContainer}>
            <FlexPane config={config.eventHeaderContainer}>
                <Button
                    class={'ta-EventHeaderButton'}
                    hrefLink={linkToEvent}
                    onClick={onHeaderClick}
                    config={config.eventNameButton}
                >
                    {showEventName ? (
                        <Text config={config.eventNameLabel}>
                            {EventFormatter.formatEventName(eventItem.name)}
                        </Text>
                    ) : (
                        <ParticipantIconsInline
                            participants={participants}
                            sportId={sportId}
                            separator={stringResolver.resolveString(
                                getSeparatorTranslationKey(eventItem.homeAway)
                            )}
                            config={config.participantsIconInline}
                        />
                    )}
                    <FlexPane config={config.matchDetailsContainer}>
                        <EventTimingStatus
                            eventItem={eventItem}
                            config={config.eventTimingStatus}
                            hasStreaming={VideoStreamsUtils.isStreamAvailable(eventItem)}
                            hasClockDetails={EventsUtils.hasInPlayClockDetails(eventItem)}
                            inplayTime={EventsUtils.getEventInplayTime(eventItem)}
                        />
                    </FlexPane>
                </Button>
            </FlexPane>
            <PriceBoostMarketContent
                config={config.marketContent}
                aggregatedMarket={boostedAggregatedMarket}
                eventItem={eventItem}
            />
        </FlexPane>
    );
};

const useBoostedContentPreload = (eventGroups, instanceId) => {
    const boostedEventIds = useMemo(
        () => eventGroups?.flatMap(group => group.events.map(event => event.id)),
        [eventGroups]
    );
    const boostedEventsLoadDone = useEventsLoadDone(boostedEventIds);

    const eventItems = useSelector(
        state =>
            boostedEventsLoadDone ? pick(selectEvents(state), ...boostedEventIds) : EMPTY_OBJECT,
        shallowEqual
    );
    const boostedMarketIds = useMemo(
        () => Object.values(eventItems).flatMap(event => event.boostedMarketIds),
        [eventItems]
    );

    // Preload all events and markets in one chunk
    useEventsChunkRequest(boostedEventIds, instanceId);
    useMarketsChunkRequest(boostedMarketIds, instanceId);

    const boostedMarketsLoadDone = useMarketsLoadDone(boostedMarketIds);

    return boostedEventsLoadDone && boostedMarketsLoadDone;
};

export default PriceBoostCoupon;
